/**
 * The study's brand theme in the widget.
 *
 * GET /surveys/{id}/branding carries `widget_theme`: --dtro-* variables the
 * server derived from the theme the interview pages use
 * (deutero_interviews/domain/theme.widget_theme), so defaults and contrast
 * are decided in one place. The widget turns it into a stylesheet for its
 * shadow root, keyed on the host's `data-dtro-scheme`, which the widget sets
 * from the study's default mode and the visitor's device.
 *
 * Precedence, lowest first: the widget's defaults (styles.ts), the study's
 * theme (`:host` rules here), then the integrator — `config.theme` and any
 * host-page CSS on the host element both outrank `:host` rules.
 */
import type { WidgetTheme } from "./types";

export type Scheme = "light" | "dark";

const VAR_NAME = /^--dtro-[a-z][a-z-]*$/;
// Colours, font stacks, lengths, numbers and color-mix(): nothing that can
// end a declaration or rule, escape, or fetch.
const VAR_VALUE = /^[#A-Za-z0-9 ,.%()"'-]{1,200}$/;
const FONTS_PREFIX = "https://fonts.googleapis.com/css2?";

function declarations(vars: Record<string, unknown> | null | undefined): string {
  return Object.entries(vars ?? {})
    .filter(([name, value]) =>
      VAR_NAME.test(name) && typeof value === "string" && VAR_VALUE.test(value) &&
      !/url\s*\(/i.test(value))
    .map(([name, value]) => `${name}:${value}`)
    .join(";");
}

/** The study theme's stylesheet ("" for none). */
export function brandThemeCss(theme: WidgetTheme | null | undefined): string {
  if (!theme) return "";
  const rules: string[] = [];
  const base = declarations(theme.base);
  if (base) rules.push(`:host{${base}}`);
  for (const scheme of ["light", "dark"] as const) {
    const decls = declarations(theme[scheme]);
    if (decls) rules.push(`:host([data-dtro-scheme="${scheme}"]){${decls}}`);
  }
  return rules.join("\n");
}

/** The theme's Google Fonts stylesheet URL, or null for anything else. */
export function safeFontsHref(href: string | null | undefined): string | null {
  if (typeof href !== "string" || !href.startsWith(FONTS_PREFIX)) return null;
  if (/["'<>\s\\]/.test(href)) return null;
  return href;
}

/** The scheme to paint: the study's default when it sets one, else the
 *  visitor's device. */
export function resolveScheme(defaultMode: string | null | undefined): Scheme {
  if (defaultMode === "light" || defaultMode === "dark") return defaultMode;
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light";
  } catch {
    return "light";
  }
}
